import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateLeaveStatus, updateClaimStatus } from '../../../../store/slices/approvalsSlice';

const ApprovalModal = ({ 
  isOpen, 
  onClose, 
  item, 
  type, 
  isUpdating 
}) => {
  const [managerRemarks, setManagerRemarks] = useState('');
  const [processingAction, setProcessingAction] = useState(null); // 'approve' or 'reject'
  const dispatch = useDispatch();

  if (!isOpen || !item) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const handleClose = () => {
    setManagerRemarks('');
    setProcessingAction(null);
    onClose();
  };

  const handleAction = async (action) => {
    setProcessingAction(action);
    
    const payload = {
      managerRemarks,
      status: action === 'approve' ? 'Approved' : 'Rejected'
    };

    try {
      if (type === 'leave') {
        await dispatch(updateLeaveStatus({
          leaveId: item.id,
          ...payload
        }));
      } else if (type === 'claim') {
        await dispatch(updateClaimStatus({
          claimId: item.id,
          ...payload
        }));
      }
      handleClose();
    } catch (error) {
      console.error('Error processing action:', error);
      setProcessingAction(null);
    }
  };

  const renderLeaveDetails = () => (
    <>
      <p><strong>Employee:</strong> {item.employeeName || item.name || 'Not specified'}</p>
      <p><strong>Email:</strong> {item.email || item.empEmail || 'Not specified'}</p>
      <p><strong>Employee ID:</strong> {item.empID || 'Not specified'}</p>
      <p><strong>Leave Type:</strong> {item.leaveType || 'Not specified'}</p>
      <p><strong>From:</strong> {formatDate(item.startDate)}</p>
      <p><strong>To:</strong> {formatDate(item.endDate)}</p>
      <p><strong>Total Days:</strong> {item.leaveDays || 'Not specified'}</p>
      <p><strong>Applied On:</strong> {formatDate(item.appliedDate || item.startDate)}</p>
      <p><strong>Current Status:</strong> <span className={`status-badge status-${item.status.toLowerCase()}`}>{item.status}</span></p>
      <p><strong>Employee Remarks:</strong> {item.remarks || item.leaveRemarks || 'None'}</p>
    </>
  );

  const renderClaimDetails = () => (
    <>
      <p><strong>Employee:</strong> {item.employeeName || item.name || 'Not specified'}</p>
      <p><strong>Email:</strong> {item.email || item.empEmail || 'Not specified'}</p>
      <p><strong>Employee ID:</strong> {item.empID || 'Not specified'}</p>
      <p><strong>Claim Type:</strong> {item.claimType || item.expensePurpose || item.title || 'Not specified'}</p>
      <p><strong>Amount:</strong> {item.currency || '$'} {item.amount ? parseFloat(item.amount).toFixed(2) : '0.00'}</p>
      <p><strong>Date:</strong> {formatDate(item.date || item.expenseDate)}</p>
      <p><strong>Applied On:</strong> {formatDate(item.submittedDate || item.claimDate)}</p>
      <p><strong>Current Status:</strong> <span className={`status-badge status-${item.status.toLowerCase()}`}>{item.status}</span></p>
      <p><strong>Description:</strong> {item.description || item.remarks || 'None'}</p>
      {(item.documentUrl || item.supportingDoc) && (
        <p>
          <strong>Document:</strong> 
          <a 
            href={item.documentUrl || item.supportingDoc} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="view-doc-link"
          >
            View Document
          </a>
        </p>
      )}
    </>
  );

  return (
    <div className="modal-overlay">
      <div className="approval-modal">
        <div className="modal-header">
          <h3>
            {type === 'leave' ? 'Leave Request' : 'Claim Request'} Details
          </h3>
          <button className="close-button" onClick={handleClose}>
            &times;
          </button>
        </div>
        
        <div className="modal-body">
          <div className="approval-details">
            {type === 'leave' ? renderLeaveDetails() : renderClaimDetails()}
            
            {item.status.toLowerCase() === 'pending' && (
              <div className="form-group">
                <label htmlFor="managerRemarks">Manager Remarks:</label>
                <textarea
                  id="managerRemarks"
                  value={managerRemarks}
                  onChange={(e) => setManagerRemarks(e.target.value)}
                  placeholder="Add your remarks here..."
                  rows={4}
                />
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          {item.status.toLowerCase() === 'pending' && (
            <>
              <button
                className="approve-button"
                onClick={() => handleAction('approve')}
                disabled={isUpdating}
              >
                {processingAction === 'approve' && (
                  <span className="spinner"></span>
                )}
                Approve
              </button>
              
              <button
                className="reject-button"
                onClick={() => handleAction('reject')}
                disabled={isUpdating}
              >
                {processingAction === 'reject' && (
                  <span className="spinner"></span>
                )}
                Reject
              </button>
            </>
          )}
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isUpdating}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal; 